import React, { useEffect, useState } from 'react';
import type { Language } from '../types';
import { getTranslation } from '../utils/translations';
import XIcon from './icons/XIcon';
import {
    canPromptInstall,
    hasCookieConsent,
    isIOS,
    markInstallDismissed,
    recordVisit,
    registerServiceWorker,
    shouldShowInstallPrompt,
    subscribeInstallAvailability,
    triggerInstallPrompt,
    getVisitCount,
} from '../utils/pwaInstall';

interface InstallAppPromptProps {
    language?: Language;
    isAuthenticated: boolean;
}

const SHOW_DELAY_MS = 6000;
const MIN_VISITS = 1;

const InstallAppPrompt: React.FC<InstallAppPromptProps> = ({ language, isAuthenticated }) => {
    const [visible, setVisible] = useState(false);
    const [mode, setMode] = useState<'native' | 'ios'>('native');
    const [canInstallNatively, setCanInstallNatively] = useState(canPromptInstall());
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(language, key);

    useEffect(() => {
        if (!isAuthenticated) return;

        recordVisit();
        void registerServiceWorker();

        setCanInstallNatively(canPromptInstall());
        const unsubscribe = subscribeInstallAvailability(() => {
            setCanInstallNatively(canPromptInstall());
        });
        return unsubscribe;
    }, [isAuthenticated]);

    useEffect(() => {
        if (!isAuthenticated || !shouldShowInstallPrompt()) return;

        const tryShow = () => {
            if (!hasCookieConsent()) return;
            if (getVisitCount() < MIN_VISITS) return;

            if (canPromptInstall()) {
                setMode('native');
                setVisible(true);
                return;
            }

            if (isIOS()) {
                setMode('ios');
                setVisible(true);
            }
        };

        const delayId = window.setTimeout(tryShow, SHOW_DELAY_MS);
        const consentPollId = window.setInterval(() => {
            if (hasCookieConsent()) {
                window.clearInterval(consentPollId);
                tryShow();
            }
        }, 1000);

        return () => {
            window.clearTimeout(delayId);
            window.clearInterval(consentPollId);
        };
    }, [isAuthenticated, canInstallNatively]);

    const handleDismiss = () => {
        markInstallDismissed();
        setVisible(false);
    };

    const handleInstall = async () => {
        await triggerInstallPrompt();
        setVisible(false);
    };

    if (!visible) return null;

    return (
        <div className="fixed bottom-24 left-4 right-4 z-[95] max-w-md mx-auto animate-fade-in pointer-events-none">
            <div className="pointer-events-auto bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-2xl p-4 flex gap-3 items-start">
                <div className="w-11 h-11 rounded-xl bg-brand-sage/15 text-brand-sage flex items-center justify-center flex-shrink-0 font-nunito font-black text-lg">
                    mP
                </div>
                <div className="flex-1 min-w-0">
                    <p className="font-black text-sm text-slate-900 dark:text-slate-100 leading-snug">
                        {t('installAppTitle')}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
                        {mode === 'ios' ? t('installAppHintIOS') : t('installAppBody')}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-3">
                        {mode === 'native' && (
                            <button
                                onClick={handleInstall}
                                className="px-4 py-2 bg-brand-sage text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-md active:scale-95 transition-all"
                            >
                                {t('installAppAction')}
                            </button>
                        )}
                        <button
                            onClick={handleDismiss}
                            className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-xl text-xs font-bold hover:bg-slate-200 dark:hover:bg-slate-700 transition-all"
                        >
                            {t('installAppLater')}
                        </button>
                    </div>
                </div>
                <button
                    onClick={handleDismiss}
                    className="p-1 text-slate-300 hover:text-slate-500 dark:hover:text-slate-300 transition-colors flex-shrink-0"
                    aria-label={t('dismiss')}
                >
                    <XIcon className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};

export default InstallAppPrompt;
